import {
  CoreInputModelUtil,
  CoreModel,
  getNewErrors,
  InputErrorModel,
  TaxExemptSection,
} from "@mindmemobile/mm-shared-global";
import { convertFromRaw, convertToRaw, EditorState } from "draft-js";
import { autorun } from "mobx";
import { CategoryModel } from "./CategoryModels";
import { MediaModel } from "./MediaModel";
import { ItemTypes, WeightUnits } from "../enums";
import { CountryWithStateModel } from "./CountryModels";
import { IAPIItemDetailPageCommonModel } from "../interfaces";

export class ItemDetailInputModel extends CoreModel {
  name: string;
  isActive: boolean;
  description: string;
  category: CategoryModel | null;
  surcharge: boolean;
  discount: boolean;
  review: boolean;
  tax: boolean;
  return: boolean;
  hasVariants: boolean;
  sku: string;
  taxId: string;
  barCode: string;
  medias: MediaModel[];
  editorState: EditorState;
  returnDays: string;
  errors: InputErrorModel<ItemDetailInputModel>[];
  descriptionEditorRawContent: string;
  donationPaidTo: string;
  taxExemptSection: string;
  showExample: boolean;
  weight: string;
  weightUnit: keyof typeof WeightUnits;
  selectedCountry: CountryWithStateModel;
  showLibrary: boolean;
  isTaxIdRequired: boolean;
  constructor(data?: Partial<ItemDetailInputModel>) {
    super();
    this.name = (data && data.name) || "";
    this.isActive = (data && data.isActive) ?? true;

    if (!data) {
      this.isActive = true;
    }
    this.category = (data && data.category) || null;
    this.sku = (data && data.sku) || "";
    this.barCode = (data && data.barCode) || "";
    this.weight = (data && data.weight) || "";
    this.weightUnit = (data && data.weightUnit) || WeightUnits.lb;
    this.selectedCountry =
      (data && data.selectedCountry) || new CountryWithStateModel();
    this.discount = (data && data.discount) || false;
    this.surcharge = (data && data.surcharge) || false;
    this.tax = (data && data.tax) || false;
    this.return = (data && data.return) || false;
    this.review = (data && data.review) || false;
    this.hasVariants = (data && data.hasVariants) || false;
    this.editorState = (data && data.editorState) || EditorState.createEmpty();
    this.description = (data && data.description) || "";
    this.medias = (data && data.medias?.map((m) => new MediaModel(m))) || [];
    this.returnDays = data?.returnDays ? data.returnDays : "";
    this.descriptionEditorRawContent =
      (data && data.descriptionEditorRawContent) || "";
    this.taxId = (data && data.taxId) || "";
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<ItemDetailInputModel>(e);
        })) ||
      [];
    this.donationPaidTo = (data && data.donationPaidTo) || "";
    this.taxExemptSection =
      (data && data.taxExemptSection) ?? TaxExemptSection.Section501c3;
    this.showExample = (data && data.showExample) ?? true;
    this.showLibrary = data?.showLibrary || false;
    this.isTaxIdRequired = data?.isTaxIdRequired || false;

    autorun(() => {
      this.validate(true);
    });
  }

  c501C3() {
    return "501(c)3";
  }

  hasError(field: keyof ItemDetailInputModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof ItemDetailInputModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  validate(isAutoRun = false, itemType?: keyof typeof ItemTypes): boolean {
    const newErrors: InputErrorModel<ItemDetailInputModel>[] = [];
    this.validateTextField(this.name, "name", newErrors);
    if (this.return) {
      this.validateNumber(parseFloat(this.returnDays), "returnDays", newErrors);
    }

    if (this.weight) {
      this.validateDecimalValue(this.weight, "weight", newErrors);
    }

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createAPIModel(): IAPIItemDetailPageCommonModel {
    return {
      id: "",
      name: this.name,
      isActive: this.isActive,
      description: this.description,
      category: this.category ? this.category.createAPIModel() : null,
      discount: this.discount,
      tax: this.tax,
      review: this.review,
      return: this.return,
      returnDays: this.returnDays === "" ? 0 : parseFloat(this.returnDays),
      sku: this.sku,
      barCode: this.barCode,
      surcharge: this.surcharge,
      descriptionEditorRawContent: this.editorState
        ? JSON.stringify(convertToRaw(this.editorState.getCurrentContent()))
        : "",
      medias: this.medias?.map((m) => m.createAPIModel()) || [],
      hasVariants: this.hasVariants,
      taxId: this.taxId,
      donationPaidTo: this.donationPaidTo,
      taxExemptSection: this.taxExemptSection,
      weight: this.weight || null,
      weightUnit: this.weightUnit,
      originCountryId: this.selectedCountry.id,
    };
  }

  static deserializeAPIModel(
    data: IAPIItemDetailPageCommonModel,
    isTaxIdRequired: boolean = false
  ): ItemDetailInputModel {
    let editorState: EditorState | undefined = undefined;

    if (data.descriptionEditorRawContent) {
      const v = data.descriptionEditorRawContent?.toString();
      const es = convertFromRaw(JSON.parse(v));
      editorState = EditorState.createWithContent(es);
    }

    return new ItemDetailInputModel({
      name: data.name,
      isActive: data.isActive,
      description: data.description,
      category: data.category
        ? CategoryModel.deserializeAPIModel(data.category)
        : null,
      discount: data.discount,
      tax: data.tax,
      review: data.review,
      return: data.return,
      returnDays: data.returnDays?.toString(),
      sku: data.sku,
      barCode: data.barCode,
      surcharge: data.surcharge,
      editorState: editorState,
      hasVariants: data.hasVariants,
      descriptionEditorRawContent: data.descriptionEditorRawContent,
      medias:
        data.medias &&
        data.medias.map((m) => MediaModel.deserializeAPIModel(m)),
      taxId: data.taxId,
      donationPaidTo: data.donationPaidTo,
      taxExemptSection: data.taxExemptSection,
      weight: !data.weight ? "" : data.weight.toString(),
      weightUnit: data.weightUnit as keyof typeof WeightUnits,
      selectedCountry: new CountryWithStateModel({ id: data.originCountryId }),
      isTaxIdRequired: isTaxIdRequired,
    });
  }

  toggleShowExample() {
    this.showExample = !this.showExample;
  }

  toggleShowLibrary() {
    this.showLibrary = !this.showLibrary;
  }
}
